import { useReactions } from '@gik/api/stream/useStream';
import { useUser } from '@gik/api/users/user';
import { SignInFlowCalloutBlock } from '@gik/auth/components/SignInFlow/SignInFlowCallout';
import { SignInFlowContentCopyVariant } from '@gik/auth/components/SignInFlow/SignInFlowStartContent';
import { signin } from '@gik/auth/utils/auth';
import type { BaseUserWithAvatar } from '@gik/core/models/gik/User';
import { useUserStore } from '@gik/core/store/UserStore';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import noop from '@gik/core/utils/noop';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { useInkindStore } from '@gik/inkind-page/store/InkindStore';
import type { Reaction, StreamComment } from '@gik/stream/types/Stream';
import type { StreamParticipant } from '@gik/stream/types/StreamParticipant';
import { getReactionIconName } from '@gik/stream/utils/findUserReaction';
import { parseStreamContent } from '@gik/stream/utils/parseStreamContent';
import { renderReactionIcon, renderReactionLabel } from '@gik/stream/utils/reactions';
import { Avatar } from '@gik/ui/Avatar';
import { Popover } from '@gik/ui/Popover';
import { PopoverMenu } from '@gik/ui/PopoverMenu';
import { Text } from '@gik/ui/Text/Text';
import { UI } from '@gik/ui/UIManager';
import { openUserInfoModal } from '@gik/user-profile/components/UserInfoModal/UserInfoModal';
import PencilIcon from '@heroicons/react/solid/PencilIcon';
import TrashIcon from '@heroicons/react/solid/TrashIcon';
import moment from 'moment';
import React from 'react';
import { Button } from '../../../ui/Button';
import type { ReactionEmojiKey } from '../../../ui/OpenMoji';
import { ReactionEmojis } from '../../../ui/OpenMoji';
import type { StreamReactionsContextProps } from '../Posts/StreamReactions';
import { StreamReactionsContext } from '../Posts/StreamReactions';
import type { onDeleteReactionCallback, onReactionCallback } from '../Reactions/ReactionButton';
import { ReactionButton } from '../Reactions/ReactionButton';
import { CommentInput } from './CommentInput';

export type onDeleteCommentCallback = (id: string) => Promise<void>;

export type CommentProps = {
  comment: Reaction<StreamComment>;
  isPageOwnerOrOrganizer: boolean;
  disabled: boolean;
  highlighted: boolean;
  users: StreamParticipant[];
  error: string;
  parentIsReaction: boolean;
  onReply?: () => void;
  onEditSubmit?: (id: string, value: string) => void;
  onEditSuccess?: (id: string, value: string) => void;
  onReaction?: onReactionCallback;
  onDelete?: onDeleteCommentCallback;
  onDeleteReaction?: onDeleteReactionCallback;
  onCreateRetry?: (id: string, value: string) => void;
  onUpdateRetry?: (id: string, value: string) => void;
  onDeleteRetry?: (id: string) => void;
} & UIComponent;

function CommentComp({
  comment,
  isPageOwnerOrOrganizer,
  children,
  className,
  users,
  disabled,
  highlighted,
  error,
  parentIsReaction,
  onReply = noop,
  onReaction = noop,
  onDelete,
  onDeleteReaction = noop,
  onEditSubmit = noop,
  onEditSuccess = noop,
  onCreateRetry,
  onUpdateRetry,
  onDeleteRetry,
  ...otherProps
}: React.PropsWithChildren<CommentProps>): React.ReactElement {
  const bem = useBemCN('comment');

  const routeId = useInkindStore(store => store.inkindRouteId);
  const userId = useUserStore(state => state.id);

  const isCommentOwner = comment.user_id === userId;

  const context = React.useContext<StreamReactionsContextProps>(StreamReactionsContext);
  const participants = context?.participants;

  const { data: userReactions, mutate: mutateUserReactions } = useReactions('user', userId, { allPages: true });

  const [isEditing, setIsEditing] = React.useState<boolean>(false);
  const [editingValue, setEditingValue] = React.useState<string>(undefined);
  const [moreOpen, setMoreOpen] = React.useState<boolean>(false);
  const [selected, setSelected] = React.useState<ReactionEmojiKey>(undefined);

  const selectedReaction = userReactions?.find(r => r.parent === comment.id && r.kind !== 'comment');
  const selectedReactionId = selectedReaction?.id;

  React.useEffect(() => {
    if (!selected && selectedReaction) {
      setSelected(getReactionIconName(selectedReaction));
    }
  }, [selected, selectedReaction, comment]);

  const { data: commentOwner } = useUser(comment.data.userId as string);

  const [reactionCounts, setReactionCounts] = React.useState<Record<string, number>>(
    // eslint-disable-next-line
    comment.children_counts as any as Record<string, number>
  );

  const data: StreamComment = comment.data as StreamComment;
  const content = data?.message;
  const parsedContent = React.useMemo(() => parseStreamContent(content, participants), [content, participants]);

  function renderReactions(forId: string, reaction_counts: Record<string, number>) {
    const likeReactionEntries = reaction_counts
      ? Object.entries(reaction_counts).filter(entry => Object.keys(ReactionEmojis).indexOf(entry[0]) >= 0)
      : [];
    if (!likeReactionEntries?.length) return null;

    const totalReactions = likeReactionEntries.map(entry => entry[1]).reduce((prev, current) => prev + current);
    if (!totalReactions) return null;
    return (
      <div key={forId} {...bem('reactions')}>
        {likeReactionEntries
          .filter(entry => entry[1] > 0) // filter out empty reactions
          .sort((a, b) => b[1] - a[1]) // show most chosen emoji first
          .slice(0, 2)
          .map((entry, index) => (
            <span key={`${entry[0]}-${index}`}>{renderReactionIcon(entry[0] as ReactionEmojiKey, true)}</span>
          ))}

        {totalReactions > 0 && <div {...bem('reactions-total')}>{totalReactions}</div>}
      </div>
    );
  }

  const handleEdit = React.useCallback(() => {
    // if (editingValue === undefined) return;
    setEditingValue(data.message);
    setIsEditing(true);
    setMoreOpen(false);
  }, [data]);

  const handleEditCancel = React.useCallback(() => {
    setEditingValue(undefined);
    setIsEditing(false);
  }, []);

  const handleEditSubmit = React.useCallback(
    async (forId: string, value: string) => {
      onEditSubmit(forId, value);
      setEditingValue(undefined);
      setIsEditing(false);
    },
    [onEditSubmit]
  );

  const handleDelete = React.useCallback(async () => {
    setMoreOpen(false);

    const response = await UI.confirm(
      `Are you sure you want to delete this ${parentIsReaction ? 'reply' : 'comment'} ?`,
      {
        title: 'Confirm Delete',
        okText: 'Delete',
        okButtonProps: {
          variant: 'danger',
        },
      }
    );
    if (!response) return;

    onDelete?.(comment.id);
  }, [comment, parentIsReaction, onDelete]);

  const handleReaction: onReactionCallback = React.useCallback(
    async (forId: string, name: ReactionEmojiKey) => {
      if (!userId) {
        const userId = await signin({
          copyVariant: !routeId ? SignInFlowContentCopyVariant.GENERAL : SignInFlowContentCopyVariant.INKIND_PAGE,
          callout: SignInFlowCalloutBlock.MESSAGE_OR_COMMENT_ATTEMPT,
          routeId,
        });
        if (!userId) {
          return;
        }
      }

      setSelected(name);

      // optimistically update reaction counters
      const newReactionCounts = JSON.parse(JSON.stringify(reactionCounts));
      // remove user's old reaction
      if (selected && selected !== name) {
        newReactionCounts[selected] = newReactionCounts[selected] - 1;
      }
      // add the new reaction the user has selected
      newReactionCounts[name] = (newReactionCounts[name] || 0) + 1;
      setReactionCounts(newReactionCounts);
    },
    [reactionCounts, routeId, selected, userId]
  );

  const handleReactionSuccess = React.useCallback(
    (forId: string, name: ReactionEmojiKey) => {
      mutateUserReactions();
      return onReaction?.(forId, name);
    },
    [onReaction, mutateUserReactions]
  );

  const handleDeleteReaction: onDeleteReactionCallback = React.useCallback(async () => {
    if (!userId) {
      const userId = await signin({
        copyVariant: !routeId ? SignInFlowContentCopyVariant.GENERAL : SignInFlowContentCopyVariant.INKIND_PAGE,
        callout: SignInFlowCalloutBlock.MESSAGE_OR_COMMENT_ATTEMPT,
        routeId,
      });
      if (!userId) {
        return;
      }
    }

    mutateUserReactions(
      userReactions.filter(reaction => reaction !== selectedReaction),
      false
    );
    setSelected(null);

    // remove the reaction the user currently has selected
    const newReactionCounts = JSON.parse(JSON.stringify(reactionCounts));
    newReactionCounts[selected] = newReactionCounts[selected] - 1;
    setReactionCounts(newReactionCounts);
  }, [userId, mutateUserReactions, userReactions, reactionCounts, selected, routeId, selectedReaction]);

  const handleDeleteReactionSuccess = React.useCallback(
    (forId: string, name: ReactionEmojiKey) => {
      mutateUserReactions();
      return onDeleteReaction?.(forId, name);
    },
    [onDeleteReaction, mutateUserReactions]
  );

  const handleBlock = React.useCallback(() => {
    UI.alert('todo');
  }, []);

  function renderError(error: string) {
    switch (error) {
      case 'create_fail':
        return (
          <span>
            <span>Failed to create comment.</span>{' '}
            <Button variant="danger-link" onClick={() => onCreateRetry(comment.id, comment.data.message as string)}>
              Try again
            </Button>
          </span>
        );
      case 'update_fail':
        return (
          <span>
            <span>Failed to update comment.</span>{' '}
            <Button variant="danger-link" onClick={() => onUpdateRetry(comment.id, comment.data.message as string)}>
              Try again
            </Button>
          </span>
        );
      case 'delete_fail':
        return (
          <span>
            <span>Failed to delete comment.</span>{' '}
            <Button variant="danger-link" onClick={() => onDeleteRetry(comment.id)}>
              Try again
            </Button>
          </span>
        );
      case 'undelete_fail':
        return (
          <span>
            <span>Failed to undelete comment.</span>{' '}
            <Button variant="danger-link" onClick={() => onDeleteRetry(comment.id)}>
              Try again
            </Button>
          </span>
        );
      default:
        return 'unknown error';
    }
  }

  React.useEffect(() => {
    // eslint-disable-next-line
    setReactionCounts(comment.children_counts as any as Record<string, number>);
  }, [comment]);

  let user: BaseUserWithAvatar = {
    fullName: '-',
    avatarThumbSmallURL: null,
    avatarThumbMediumURL: null,
    avatarURL: null,

    id: data.userId,
    // fullName: data.userFullName,
    // avatarThumbSmallURL: data.userAvatarThumbSmallURL,
    // avatarThumbMediumURL: data.userAvatarThumbMediumURL,
    // avatarURL: data.userAvatarThumbMediumURL,
    emailAddress: null,
  };

  if (commentOwner) user = commentOwner;

  return (
    <div {...bem(null, [{ editing: isEditing }, { highlighted }], className)} {...otherProps}>
      {data && <Avatar imageUrl={user.avatarThumbSmallURL} onClick={() => openUserInfoModal(user)} />}
      <div {...bem('content-wrapper')}>
        <div {...bem('content')}>
          <a {...bem('username')} onClick={() => openUserInfoModal(user)}>
            <Text>{user.fullName}</Text>
          </a>
          &nbsp;
          {!isEditing && <Text {...bem('text')}>{parsedContent}</Text>}
          {isEditing && (
            <div {...bem('editor')}>
              <CommentInput
                autoFocus
                value={editingValue}
                onCancel={handleEditCancel}
                onChange={({ target }) => {
                  if (isEditing) setEditingValue(target.value);
                }}
                onSubmit={handleEditSubmit}
                forId={comment.id}
                variant="neutral"
                users={users}
              />
            </div>
          )}
          {renderReactions(comment.id, reactionCounts)}
        </div>

        <div {...bem('footer')}>
          {isEditing && <span {...bem('editing-message')}>Press esc to cancel</span>}

          {!error && <span {...bem('date')}>{moment(comment.created_at).fromNow()}</span>}

          {error && (
            <span {...bem('error')}>
              <span {...bem('comment-error')}>{renderError(error)}</span>
            </span>
          )}

          {userId && !error && (
            <ul {...bem('actions')}>
              <li>
                <ReactionButton
                  disabled={disabled}
                  forId={comment.id}
                  isReaction
                  mini
                  variant="default-link"
                  onReaction={handleReaction}
                  onReactionSuccess={handleReactionSuccess}
                  onDeleteReaction={handleDeleteReaction}
                  onDeleteReactionSuccess={handleDeleteReactionSuccess}
                  selected={selected}
                  // eslint-disable-next-line
                  selectedReactionId={selectedReactionId}
                  {...bem('btn-react', [{ [selected]: selected }])}
                >
                  {renderReactionLabel(selected)}
                </ReactionButton>
              </li>
              <li>
                <Button
                  disabled={disabled}
                  variant="default-link"
                  {...bem('btn-reply')}
                  onClick={() => {
                    if (disabled) return;
                    onReply();
                  }}
                >
                  Reply
                </Button>
              </li>
              {((userId && comment.data.userId === userId) || isPageOwnerOrOrganizer) && (
                <li>
                  <Popover
                    noPad
                    placement="bottom-start"
                    {...bem('more-menu')}
                    trigger={
                      <Button disabled={disabled} variant="default-link" {...bem('btn-more')}>
                        More
                      </Button>
                    }
                    isOpen={moreOpen}
                    triggerOnClick={() => {
                      if (disabled) return;
                      setMoreOpen(true);
                    }}
                    onClose={() => setMoreOpen(false)}
                  >
                    <PopoverMenu>
                      {isCommentOwner && (
                        <>
                          <PopoverMenu.Item onClick={handleEdit} icon={PencilIcon}>
                            Edit
                          </PopoverMenu.Item>
                        </>
                      )}
                      {isCommentOwner && (
                        <>
                          <PopoverMenu.Item onClick={handleDelete} icon={TrashIcon}>
                            Delete
                          </PopoverMenu.Item>
                        </>
                      )}

                      {!isCommentOwner && isPageOwnerOrOrganizer && (
                        <>
                          <PopoverMenu.Item onClick={handleDelete} icon={TrashIcon}>
                            Delete
                          </PopoverMenu.Item>
                          {/* TODO */}
                          {/* <PopoverMenu.Item onClick={handleBlock} icon={PencilIcon}>
                            Block from Comments
                          </PopoverMenu.Item> */}
                        </>
                      )}
                    </PopoverMenu>
                  </Popover>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export const Comment = withComponentErrorBoundary(CommentComp);
